import type React from "react";
import { ChatState, useProject } from "@/features/project";
import { HighlightedTextarea } from "./HighlightedTextarea";

interface CodeBlockProps {
  label: string;
  code: string;
  onChange: (value: string) => void;
  readOnly: boolean;
}

const CodeBlock: React.FC<CodeBlockProps> = ({ label, code, onChange, readOnly }) => (
  <div className="flex flex-col shrink-0">
    <div className="px-4 py-1 text-slate-500 text-[10px] tracking-wider">{label}</div>
    <HighlightedTextarea
      code={code}
      onChange={onChange}
      readOnly={readOnly}
      language="javascript"
    />
  </div>
);

export const CodeEditor: React.FC = () => {
  const { files, updateProjectFiles, chatState } = useProject();

  const readOnly = chatState !== ChatState.IDLE;

  const helperEntries = Object.entries(files.helpers || {});

  return (
    <div className="h-full w-full bg-slate-900 border-r border-slate-800 font-mono text-sm overflow-hidden relative">
      <div className="h-full overflow-auto custom-scrollbar">
        <div className="min-w-fit">
          <CodeBlock
            label="init"
            code={files.init.trim()}
            onChange={(value) => updateProjectFiles({ init: value })}
            readOnly={readOnly}
          />
          <div className="mx-4 border-t border-slate-800" />
          <CodeBlock
            label="render"
            code={files.render.trim()}
            onChange={(value) => updateProjectFiles({ render: value })}
            readOnly={readOnly}
          />
          {helperEntries.map(([id, code]) => (
            <div key={id}>
              <div className="mx-4 border-t border-slate-800" />
              <CodeBlock
                label={id}
                code={code.trim()}
                onChange={(value) =>
                  updateProjectFiles({ helpers: { ...files.helpers, [id]: value } })
                }
                readOnly={readOnly}
              />
            </div>
          ))}
        </div>
      </div>
      {readOnly && (
        <div className="absolute inset-0 bg-slate-950/50 flex items-center justify-center pointer-events-none">
          <span className="px-3 py-1 bg-slate-800 text-slate-400 rounded text-xs border border-slate-700">
            AI is working...
          </span>
        </div>
      )}
    </div>
  );
};
